import { motion } from "framer-motion";
import { Dumbbell, BookOpen, Heart, Briefcase, Clock } from "lucide-react";

const PILLARS = [
  {
    icon: Dumbbell,
    number: "01",
    title: "Movement",
    tag: "3:00 – 4:00 PM",
    desc: "Strength training, conditioning, group sports, and team challenges paired with nutritional support to help build discipline, consistency, and healthy stress release.",
    bg: "bg-brand-coral text-white",
    span: "md:col-span-7",
    image: "https://images.unsplash.com/photo-1761039807514-292d7d33059f",
  },
  {
    icon: BookOpen,
    number: "02",
    title: "Academic Support",
    tag: "4:00 – 4:45 PM",
    desc: (
      <>
        Homework help, tutoring partnerships, workshops with a strong focus in{" "}
        <span className="bg-brand-yellow/70 text-brand-navy font-bold px-1.5 rounded">
          STEM
        </span>
        ,{" "}
        <span className="bg-brand-coral/30 text-brand-navy font-bold px-1.5 rounded">
          Financial literacy
        </span>
        , language arts &amp; structured study time.
      </>
    ),
    bg: "bg-white text-brand-navy",
    span: "md:col-span-5",
  },
  {
    icon: Heart,
    number: "03",
    title: "Mentorship",
    tag: "4:45 – 5:30 PM",
    desc: "Conflict resolution, emotional regulation, drug prevention, identity + purpose work, and leadership circles for teens.",
    bg: "bg-brand-navy text-white",
    span: "md:col-span-6",
    image: "https://images.unsplash.com/photo-1752650736141-2e7c25e10583",
  },
  {
    icon: Briefcase,
    number: "04",
    title: "Career Pathways",
    tag: "5:30 – 6:00 PM · 2–3× weekly",
    desc: "Guest speakers, hands-on trades intro (electrical, mechanical), entrepreneurship basics, financial literacy, and enrichment programs.",
    bg: "bg-brand-yellow text-brand-navy",
    span: "md:col-span-6",
  },
];

export default function ProgramSection() {
  return (
    <section
      id="program"
      data-testid="program-section"
      className="py-20 md:py-32 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-coral mb-4">
              The daily program
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-navy">
              Four pillars. Every weekday.{" "}
              <span className="text-slate-400">Built to change lives.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <div className="flex items-start gap-3 bg-white rounded-2xl p-5 border border-slate-200">
              <Clock className="w-5 h-5 text-brand-coral flex-shrink-0 mt-0.5" />
              <p className="text-slate-700 leading-relaxed">
                Kids arrive at <strong>3 PM</strong> after school and leave at{" "}
                <strong>6 PM</strong> — 15 hours of structured development every
                week. Monday through Friday.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.number}
                data-testid={`program-pillar-${p.number}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`col-span-1 ${p.span} ${p.bg} rounded-3xl p-8 md:p-10 relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300 min-h-[280px] flex flex-col justify-between`}
              >
                {p.image && (
                  <img
                    src={p.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-overlay"
                  />
                )}
                <div className="relative flex items-start justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                    <Icon className="w-7 h-7" />
                  </div>
                  <span className="font-display font-black text-6xl opacity-20">
                    {p.number}
                  </span>
                </div>
                <div className="relative mt-8">
                  <div className="text-xs font-bold uppercase tracking-widest opacity-70 mb-2">
                    {p.tag}
                  </div>
                  <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed opacity-90 max-w-lg">
                    {p.desc}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
